<!-- Custom Field Renderer Template -->
<div
  *ngIf="shouldRender()"
  [formGroup]="form"
  [ngClass]="getFieldClasses()"
  class="custom-field-wrapper"
>
  <!-- Text Input Field -->
  <mat-form-field
    *ngIf="field.type === 'text'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <input
      matInput
      [name]="field.name"
      [formControlName]="field.name"
      [placeholder]="getPlaceholder()"
      [type]="getInputType()"
      [style.direction]="getTextDirection()"
      [maxlength]="getMaxLength()"
      [readonly]="field.read_only"
      (input)="onInputChange($event)"
    />
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- Character count indicator for text fields -->
  <div
    *ngIf="field.type === 'text' && getMaxLength()"
    class="text-xs mt-1"
    [ngClass]="{
      'text-gray-500': !isApproachingLimit() && !hasReachedLimit(),
      'text-orange-500': isApproachingLimit(),
      'text-red-500 font-medium': hasReachedLimit()
    }"
  >
    {{ getCharCountMessage() }}
  </div>

  <!-- Textarea Field -->
  <mat-form-field
    *ngIf="field.type === 'textarea'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <textarea
      matInput
      [name]="field.name"
      [formControlName]="field.name"
      [placeholder]="getPlaceholder()"
      [style.direction]="getTextDirection()"
      [maxlength]="getMaxLength()"
      [readonly]="field.read_only"
      rows="4"
      cdkTextareaAutosize
      [cdkAutosizeMinRows]="4"
      [cdkAutosizeMaxRows]="8"
      (input)="onInputChange($event)"
    >
    </textarea>
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- Character count indicator for textarea fields -->
  <div
    *ngIf="field.type === 'textarea' && getMaxLength()"
    class="text-xs mt-1"
    [ngClass]="{
      'text-gray-500': !isApproachingLimit() && !hasReachedLimit(),
      'text-orange-500': isApproachingLimit(),
      'text-red-500 font-medium': hasReachedLimit()
    }"
  >
    {{ getCharCountMessage() }}
  </div>

  <!-- Number Input Field -->
  <mat-form-field
    *ngIf="field.type === 'number'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <input
      matInput
      [name]="field.name"
      [formControlName]="field.name"
      [placeholder]="getPlaceholder()"
      type="text"
      inputmode="decimal"
      [style.direction]="getTextDirection()"
      [readonly]="field.read_only"
      (input)="onInputChange($event)"
      (keypress)="onNumberKeyPress($event)"
      (blur)="onNumberInputBlur($event)"
    />
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- Date Input Field -->
  <mat-form-field
    *ngIf="field.type === 'date'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <input
      matInput
      [formControlName]="field.name"
      [placeholder]="getPlaceholder()"
      [type]="getInputType()"
      [readonly]="field.read_only"
      (change)="onDateChange($event)"
    />
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- DateTime Input Field -->
  <mat-form-field
    *ngIf="field.type === 'datetime'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <input
      matInput
      [name]="field.name"
      [formControlName]="field.name"
      [placeholder]="getPlaceholder()"
      [type]="getInputType()"
      [readonly]="field.read_only"
      (change)="onDateChange($event)"
    />
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- Select Dropdown Field -->
  <mat-form-field
    *ngIf="field.type === 'select'"
    [appearance]="getMatFieldAppearance()"
    class="w-full"
  >
    <mat-label class="text-primary">{{ getLabel() }}</mat-label>
    <mat-select [name]="field.name" [formControlName]="field.name">
      <mat-option *ngIf="!field.required" [value]="''"></mat-option>
      <mat-option *ngIf="!field.options || field.options.length === 0" [disabled]="true">
        {{ field.empty_message || ('No data' | transloco) }}
      </mat-option>
      <mat-option
        *ngFor="let option of field.options; trackBy: trackByOption"
        [value]="option.value"
        [disabled]="isOptionDisabled(option)"
      >
        {{ option.label }}
      </mat-option>
    </mat-select>
    <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
  </mat-form-field>

  <!-- Nested Child Field (Recursive Rendering) -->
  <!-- Only render if: 1) field is select, 2) has child field, 3) child field's control exists in form -->
  <!-- Using ngFor with trackBy to force component recreation when field changes -->
  <div
    *ngIf="
      field.type === 'select' &&
      hasChildField() &&
      getChildField() &&
      form.contains(getChildField()!.name)
    "
    class="mt-4 ml-4 pl-4 border-l-2 border-gray-200"
  >
    <ng-container *ngFor="let childField of [getChildField()]; trackBy: trackByChildField">
      <app-custom-field-renderer [field]="childField!" [form]="form" [disabled]="disabled">
      </app-custom-field-renderer>
    </ng-container>
  </div>

  <!-- Boolean/Checkbox Field -->
  <div *ngIf="field.type === 'boolean'" class="w-full">
    <mat-checkbox [name]="field.name" [formControlName]="field.name" color="primary">
      {{ getLabel() }}
    </mat-checkbox>
    <mat-error *ngIf="hasError()" class="block mt-1 text-sm text-warn">
      {{ getErrorMessage() }}
    </mat-error>
  </div>

  <!-- Unsupported Field Type -->
  <div
    *ngIf="
      !['text', 'textarea', 'number', 'date', 'datetime', 'select', 'boolean'].includes(field.type)
    "
    class="w-full p-4 bg-warn/10 border border-warn/20 rounded-lg"
  >
    <p class="text-warn text-sm">Unsupported field type: {{ field.type }}</p>
  </div>
</div>
